import SettingsEmptyState from '../../components/settings/SettingsEmptyState';
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useCurrent, usePut } from '../../hooks'
import { BasicSelector, MwButton, MwSpinnerButton } from '../../components/ui'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { setLang } from '../../store/langSlice'
import { updateSettings } from '../../store/settingSlice'

const CENTRAL_SETTINGS_SCOPE = 'global'
const CENTRAL_SELECTION_SCOPE = 'header-selection'

const LanguagesSetting = ({ refreshTenant }) => {
    const dispatch = useDispatch()
    const [t, i18n] = useTranslation('global')
    const { currentTenantId, currentCompanyId, currentBranchId, currentLangList, currentLangId, currentSettings, user } = useCurrent()
    const { loading: saveLoading, putData } = usePut()
    const [selectedLangCode, setSelectedLangCode] = useState(currentLangId || '')
    const lastCurrentLangRef = useRef(currentLangId)

    useEffect(() => {
        const lastCurrentLang = lastCurrentLangRef.current
        if (currentLangId && currentLangId !== lastCurrentLang) {
            if (!selectedLangCode || selectedLangCode === lastCurrentLang) {
                setSelectedLangCode(currentLangId)
            }
            lastCurrentLangRef.current = currentLangId
            return
        }
        if (!selectedLangCode && currentLangList && currentLangList.length > 0) {
            setSelectedLangCode(currentLangList[0].langCode)
        }
    }, [currentLangId, currentLangList, selectedLangCode])

    const languageOptions = useMemo(() => {
        return (currentLangList || []).map(lang => ({
            label: lang.langCode === 'ar' ? 'عربي' : lang.langCode === 'en' ? 'English' : (lang.langName || lang.langCode),
            value: lang.langCode,
            langName: lang.langName || lang.langCode,
            langCode: lang.langCode
        }))
    }, [currentLangList])

    const selectedLang = useMemo(() => {
        return languageOptions.find(option => option.value === selectedLangCode)
    }, [languageOptions, selectedLangCode])

    const onDefaultLangSelected = (item) => {
        if (!item) return
        setSelectedLangCode(item.value)
    }

    const saveHandler = async () => {
        if (!currentTenantId) return
        if (selectedLang?.langCode && currentCompanyId) {
            const nextGeneralSettings = {
                ...(currentSettings?.generalSettings || {}),
                DEFAULT_LANG: selectedLang.langCode
            }
            const settingsPayload = {
                tenantId: currentTenantId,
                companyId: currentCompanyId,
                scope: CENTRAL_SETTINGS_SCOPE,
                data: {
                    invoiceSettings: currentSettings?.invoiceSettings || {},
                    generalSettings: nextGeneralSettings,
                    quoteSettings: currentSettings?.quoteSettings || {}
                }
            }
            await putData(`${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central`, settingsPayload)
            if (user?.tenant_id && user?.company_id) {
                await putData(`${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central`, {
                    tenantId: user.tenant_id,
                    companyId: user.company_id,
                    scope: CENTRAL_SELECTION_SCOPE,
                    data: {
                        selectedCompanyId: currentCompanyId,
                        selectedBranchId: currentBranchId,
                        selectedLang: selectedLang.langCode
                    }
                })
            }
            i18n.changeLanguage(selectedLang.langCode)
            dispatch(setLang({
                label: selectedLang.langCode === 'ar' ? 'عربي' : selectedLang.langCode === 'en' ? 'English' : (selectedLang.langName || selectedLang.langCode),
                value: selectedLang.langCode
            }))
            dispatch(updateSettings({
                typeSetting: 'general',
                value: nextGeneralSettings
            }))
            lastCurrentLangRef.current = selectedLang.langCode
        }
        await refreshTenant()
    }

    if (languageOptions.length === 0) {
        return <SettingsEmptyState title={t('setting.languageSettings')} />;
    }

    return (
        <div className="settings-editable-page">
            <div className='flex justify-between items-center mb-6'>
                <h1 className="text-2xl font-bold text-gray-800">{t("setting.languageSettings")}</h1>
                {saveLoading ? <MwSpinnerButton /> : (
                    <MwButton
                        inGroup={true}
                        size='sm'
                        onClick={saveHandler}
                        type='saveBtn'
                    >
                        {t("setting.Save_changes")}
                    </MwButton>
                )}
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                <div className='bg-white p-4 rounded-lg shadow-sm border border-gray-200'>
                    <div className='text-slate-500 font-semibold mb-2'>{t("setting.DEFAULT_LANG")}</div>
                    <BasicSelector
                        key={selectedLang?.langCode || currentLangId || 'default-lang'}
                        listItem={languageOptions}
                        onSelected={onDefaultLangSelected}
                        label={selectedLang?.label || t("setting.DEFAULT_LANG")}
                        defaultValue={selectedLang?.langCode || currentLangId}
                        withArrow={true}
                        className="w-full"
                    />
                </div>
            </div>
        </div>
    )
}

export default LanguagesSetting
